// client/src/lib/api/inventory-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE INVENTARIO
// Maneja productos en inventario, paginación, cálculos, análisis, reportes y exportación.
// ===========================================

import { apiRequest, buildUrlWithParams } from "./api-client";
import type { PaginatedResponse } from "./types";

// -------------------------------------------
// Obtener inventario global (sin paginación)
// -------------------------------------------
// COMENTARIO: apiRequest obtiene la respuesta JSON completa.
// Extraemos solo el array `inventory` para mantener compatibilidad con componentes existentes.
export async function getAllInventory(): Promise<any[]> {
  const response = await apiRequest<{ inventory: any[] }>("/api/inventory");
  return response.inventory;
}

// -------------------------------------------
// Obtener inventario global con paginación
// -------------------------------------------
export async function getAllInventoryPaginated(filters?: {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: "price-asc" | "price-desc" | "name";
}): Promise<{
  data: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}> {
  const url = buildUrlWithParams("/api/inventory", filters);
  return apiRequest(url);
}

// -------------------------------------------
// Obtener inventario por tienda (sin paginación)
// -------------------------------------------
// COMENTARIO: Se mantiene el try/catch interno para retornar un array vacío en caso de error,
// garantizando que los componentes que dependen de esto no fallen por promesas rechazadas inesperadamente.
export async function getShopInventory(shopId: string): Promise<any[]> {
  try {
    const response = await apiRequest<{ data?: any[] } | any[]>(
      `/api/shops/${shopId}/inventory`,
    );
    const inventoryData = Array.isArray(response)
      ? response
      : (response as any).data || [];
    return inventoryData;
  } catch (error) {
    console.error("Error fetching shop inventory:", error);
    return [];
  }
}

// -------------------------------------------
// Obtener inventario por tienda con paginación y filtros de stock
// -------------------------------------------
export async function getShopInventoryPaginated(
  shopId: string,
  filters?: {
    page?: number;
    limit?: number;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    stockFilter?: number;
    stockOperator?: string;
  },
): Promise<{
  data: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}> {
  const url = buildUrlWithParams(`/api/shops/${shopId}/inventory`, filters);
  return apiRequest(url);
}

// -------------------------------------------
// Crear item en inventario
// -------------------------------------------
export async function createInventoryItem(item: {
  shopId: string;
  productId: string;
  price: string;
  stock: number;
  lowStockThreshold: number;
  supplierId?: string;
  supplierName?: string;
  supplierContact?: string;
  supplierNotes?: string;
  acquisitionCost?: string;
  profitMargin?: string;
  suggestedPrice?: string;
  batchNumber?: string;
  purchaseDate?: string;
  expiryDate?: string;
  manufactureDate?: string;
  minimumOrderQuantity?: number;
  leadTimeDays?: number;
  reorderPoint?: number;
  dailySalesRate?: string;
}): Promise<any> {
  const response = await apiRequest<any>("/api/inventory", {
    method: "POST",
    body: JSON.stringify(item),
  });
  return response.item;
}

// -------------------------------------------
// Actualizar item en inventario
// -------------------------------------------
export async function updateInventoryItem(
  id: string,
  updates: {
    price?: string;
    stock?: number;
    lowStockThreshold?: number;
    supplierId?: string | null;
    supplierName?: string;
    supplierContact?: string;
    supplierNotes?: string;
    acquisitionCost?: string;
    profitMargin?: string;
    suggestedPrice?: string;
    batchNumber?: string;
    purchaseDate?: string;
    expiryDate?: string;
    manufactureDate?: string;
    minimumOrderQuantity?: number;
    leadTimeDays?: number;
    reorderPoint?: number;
    dailySalesRate?: string;
    productName?: string;
    productCategory?: string;
  },
): Promise<any> {
  const response = await apiRequest<any>(`/api/inventory/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
  return response.item;
}

// -------------------------------------------
// Cálculos de inventario (Batch y por item)
// -------------------------------------------
export async function getInventoryCalculations(
  shopId: string,
  params?: { page?: number; limit?: number; search?: string },
): Promise<{
  success: boolean;
  calculations: Record<string, any>;
  summary: any;
  metadata: any;
}> {
  const url = buildUrlWithParams(
    `/api/inventory/${shopId}/calculations/batch`,
    params,
  );
  return apiRequest(url);
}

export async function getInventoryItemCalculations(
  itemId: string,
): Promise<{ success: boolean; calculations: any; item: any; metadata: any }> {
  return apiRequest(`/api/inventory/${itemId}/calculations`);
}

// -------------------------------------------
// Análisis avanzado de inventario (KPIs, alertas, recomendaciones)
// -------------------------------------------
export async function getAdvancedAnalysis(shopId: string): Promise<{
  summary: {
    totalItems: number;
    totalValueAtCost: number;
    totalValueAtPrice: number;
    averageMargin: number;
    expiringSoonCount: number;
    lowStockCount: number;
    zeroStockCount: number;
  };
  expiringSoon: any[];
  lowStockItems: any[];
  zeroStockItems: any[];
  recommendations: Array<{ type: string; message: string; priority: string }>;
}> {
  return apiRequest(`/api/inventory/${shopId}/advanced-analysis`);
}

// -------------------------------------------
// Análisis de inventario (Gráficos y resúmenes)
// -------------------------------------------
export async function getInventoryAnalysisData(shopId: string): Promise<{
  success: boolean;
  marginChartData: Array<{
    name: string;
    value: number;
    totalValue: number;
    category: string;
  }>;
  categoryChartData: Array<{
    name: string;
    productos: number;
    valor: number;
    costo: number;
    margenPromedio: number;
  }>;
  summary: {
    totalProducts: number;
    totalValue: number;
    highMarginCount: number;
    lowMarginCount: number;
  };
  metadata: any;
}> {
  return apiRequest(`/api/inventory/${shopId}/analysis`);
}

// -------------------------------------------
// Reportes de inventario
// -------------------------------------------
export async function getShopInventoryReport(
  shopId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    reportType?: "summary" | "financial" | "operations";
  },
): Promise<{
  success: boolean;
  data: {
    period: { start?: string; end?: string };
    summary: any;
    operations?: any;
    reportType: string;
    generatedAt: string;
    currency: string;
  };
  message: string;
}> {
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/reports/inventory`,
    filters,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Exportar inventario a Excel (Manejo especial para blobs)
// -------------------------------------------
// COMENTARIO: Esta función requiere `fetch` nativo porque descarga un archivo binario (`blob`),
// no un JSON. Se limpia la lógica y se estandariza el manejo de errores.
export async function exportInventoryToExcel(
  shopId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    reportType?: "summary" | "financial" | "operations" | "detailed";
  },
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const url = buildUrlWithParams(
      `/api/shops/${shopId}/reports/export/excel`,
      filters,
    );
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    });

    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error ${response.status}`);
      } catch {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const contentDisposition = response.headers.get("content-disposition");
    let filename = `inventario_${shopId}_${new Date().toISOString().split("T")[0]}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename\*?=["']?([^"']+)["']?/i);
      if (match?.[1]) filename = decodeURIComponent(match[1]);
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return {
      success: true,
      message: `Archivo "${filename}" descargado exitosamente`,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || "Error al exportar a Excel",
    };
  }
}

// -------------------------------------------
// Obtener inventario filtrado + items del carrito
// -------------------------------------------
export async function getShopInventoryWithCart(
  shopId: string,
  cartItemIds: string[],
  filters?: {
    page?: number;
    limit?: number;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  },
): Promise<{
  filtered: {
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  cartItems: any[];
}> {
  const url = `/api/shops/${shopId}/inventory/with-cart`;
  const response = await apiRequest<any>(url, {
    method: "POST",
    body: JSON.stringify({
      cartItemIds: cartItemIds || [],
      filters: {
        page: filters?.page || 1,
        limit: filters?.limit || 20,
        search: filters?.search,
        minPrice: filters?.minPrice,
        maxPrice: filters?.maxPrice,
      },
    }),
  });
  return { filtered: response.filtered, cartItems: response.cartItems || [] };
}
